import { type FC, memo, useMemo } from 'react'

import { VaultMediaType } from '@api'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'

import VaultAudioIcon from '@/assets/icons/icon-vault-audio.svg'
import VaultGifIcon from '@/assets/icons/icon-vault-gif.svg'
import VaultImageIcon from '@/assets/icons/icon-vault-image.svg'
import VaultVideoIcon from '@/assets/icons/icon-vault-video.svg'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $fan } from '@/models/chattingUI/chats/chats.ts'
import { setMessageIdToFetch } from '@/models/chattingUI/messages/messages.ts'
import { $currentCreator } from '@/models/creators.ts'
import type {
  SentMediaMessage,
  SentMediaMessageMedia,
} from '@/modules/chatting/types.ts'
import { secondsToTime } from '@/modules/chatting/ui/utils.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { VaultMediaPreview } from '@/modules/vault/ui/media/vault-media-preview.tsx'
import { classNames } from '@/utils/helpers.ts'

import CalendarIcon from '../../modules/chatting/icons/calendar.svg'
import ArrowTop from '../../modules/chatting/icons/icon-arrow-top.svg'
import TextIcon from '../../modules/chatting/icons/icon-text.svg'
import LockedIcon from '../../modules/chatting/icons/locked.svg'
import UnlockedIcon from '../../modules/chatting/icons/unlocked.svg'
import { IconButton } from '../../modules/chatting/ui/components/icon-button.tsx'

interface Props {
  media: SentMediaMessageMedia
  message: SentMediaMessage
  onClose?: () => void
}

export const ChatGalleryMediaItem: FC<Props> = memo(
  ({ media, message, onClose }) => {
    const fan = useUnit($fan)

    const creator = useUnit($currentCreator)

    const type = media.type
    const hasText = message.text && message.text.length > 0
    const byCreator = message.sentBy === 'creator'

    const avatar = useMemo(() => {
      if (byCreator) {
        return (
          <CreatorAvatar
            name={creator?.name || ''}
            avatarSrc={creator?.avatar.croppedUrl || ''}
            size={24}
            className="mr-2"
          />
        )
      }

      return (
        <CreatorAvatar
          name={fan?.displayName || ''}
          avatarSrc={
            fan?.avatarThumbs?.c50 ?? fan?.avatarThumbs?.c144 ?? fan?.avatar
          }
          size={24}
          className="mr-2"
        />
      )
    }, [
      byCreator,
      creator?.avatar.croppedUrl,
      creator?.name,
      fan?.avatar,
      fan?.avatarThumbs?.c144,
      fan?.avatarThumbs?.c50,
      fan?.displayName,
    ])

    return (
      <div className="aspect-square w-full relative overflow-hidden rounded-md">
        {media.files && (
          <VaultMediaPreview
            className="h-full"
            fit="cover"
            files={media.files}
            type={media.type as VaultMediaType}
          />
        )}
        <div className="absolute left-0 top-0 flex w-full justify-between px-1.5 py-1.5">
          <div className="relative w-fit">
            {avatar}
            {(byCreator || (!byCreator && fan?.online)) && (
              <div className="bg-success-default dark:border-secondary-700 absolute bottom-0.5 right-1.5 z-10 h-2 w-2 rounded-full border border-white" />
            )}
          </div>
          <div className="flex gap-1">
            {message.price > 0 && (
              <Tooltip
                content={
                  <div className="flex flex-col">
                    <span>
                      Sent on{' '}
                      {format(new Date(message.createdAt), 'dd.MM.yyyy')}
                    </span>
                    {message.purchaseDate && (
                      <span>
                        Bought on{' '}
                        {format(new Date(message.purchaseDate), 'dd.MM.yyyy')}
                      </span>
                    )}
                  </div>
                }
              >
                <div className="flex items-center gap-1 rounded-md bg-black/40 p-1 ">
                  <span className="text-xs text-white">${message.price}</span>
                  {message.isOpened ? (
                    <UnlockedIcon className="text-white" />
                  ) : (
                    <LockedIcon className="text-white" />
                  )}
                </div>
              </Tooltip>
            )}
            {message.isFree && (
              <Tooltip
                content={`Sent on ${format(new Date(message.createdAt), 'dd.MM.yyyy')}`}
              >
                <div className="flex items-center gap-1 rounded-md bg-black/40 p-1">
                  <CalendarIcon className="h-4 w-4 text-white" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/30 px-1.5 pb-1.5 pt-4">
          <div className="flex justify-between align-bottom">
            <ul className="flex items-center gap-2">
              {type === VaultMediaType.PHOTO && (
                <li className="flex items-center">
                  <VaultImageIcon
                    className={classNames(
                      'mr-1 h-4 w-4 stroke-white transition',
                    )}
                  />
                </li>
              )}
              {type === VaultMediaType.VIDEO && (
                <>
                  <li className="flex items-center">
                    <VaultVideoIcon
                      className={classNames('h-4 w-4 stroke-white transition')}
                    />
                  </li>
                  <li className="flex">
                    <span className="text-xs font-medium text-white">
                      {secondsToTime(Math.floor(media.duration))}
                    </span>
                  </li>
                </>
              )}
              {type === VaultMediaType.AUDIO && (
                <>
                  <li className="flex items-center">
                    <VaultAudioIcon
                      className={classNames('h-4 w-4 stroke-white transition')}
                    />
                  </li>
                  <li className="flex">
                    <span className="text-xs font-medium text-white">
                      {secondsToTime(Math.floor(media.duration))}
                    </span>
                  </li>
                </>
              )}
              {type === VaultMediaType.GIF && (
                <li className="flex items-center">
                  <VaultGifIcon
                    className={classNames(
                      'mr-1  h-4 w-4 stroke-white transition',
                    )}
                  />
                </li>
              )}
              {hasText && (
                <li className="flex items-center gap-2 text-xs text-white">
                  •{' '}
                  <Tooltip
                    contentClassName="max-w-56"
                    content={message.text}
                  >
                    <TextIcon
                      className={classNames(
                        'mr-1 h-4 w-4 stroke-white text-white transition',
                      )}
                    />
                  </Tooltip>
                </li>
              )}
            </ul>

            <IconButton
              className="bg-primary-500 text-neutral-default hover:bg-primary-600 rounded-md p-1.5 font-medium shadow"
              icon={ArrowTop}
              onClick={(event) => {
                event.stopPropagation()
                onClose?.()
                setMessageIdToFetch({ messageIdToFetch: message.id })
              }}
            />
          </div>
        </div>
      </div>
    )
  },
)

ChatGalleryMediaItem.displayName = 'ChatGalleryMediaItem'
